import imageCompression from 'browser-image-compression';

/**
 * Shared client-side image compression for every upload path in the app
 * (logbook photos, task attachments, inspection evidence, show logos,
 * profile images). Re-encodes to JPEG at quality 0.8 and bounds the long
 * edge to 2048px — saves bandwidth on cellular, shortens the upload
 * progress bar, and bounds server storage. `browser-image-compression`
 * also reads + applies EXIF orientation so rotated phone photos render
 * upright after compression instead of sideways.
 *
 * The server re-encodes again as defense-in-depth (any client that
 * skips this — curl, an outdated build, a malicious actor — still
 * lands a sane JPEG on disk). So this is purely an optimisation; the
 * pipeline doesn't depend on it.
 */

const DEFAULTS = {
    /** Long-edge clamp; bounds both portrait and landscape photos. */
    maxWidthOrHeight: 2048,
    /**
     * Soft target for output file size. The library iterates compression
     * to hit this — 1.5MB lands a high-quality phone photo under typical
     * cellular budgets while keeping detail.
     */
    maxSizeMB: 1.5,
    /**
     * JPEG quality 0.8 is the standard "good enough" knob — well above
     * the artifact threshold (~0.7), well below the diminishing-returns
     * cliff (~0.9). The library may override this when iterating to
     * meet maxSizeMB.
     */
    initialQuality: 0.8,
    /** Use a Web Worker so the main thread doesn't jank on large photos. */
    useWebWorker: true,
    /**
     * Force JPEG even for PNG/HEIC inputs. Inspection photos and logos
     * don't need transparency; JPEG is dramatically smaller for
     * photographic content. The server re-encode also normalises to
     * JPEG so the on-disk format is consistent.
     */
    fileType: 'image/jpeg'
};

/**
 * Compress an image File and return a new File ready to POST.
 * Non-image inputs and compression failures fall back to the original
 * file — never block an upload because the optimiser misbehaved.
 */
export async function compressImageFile(input: File): Promise<File> {
    if (!input.type?.startsWith('image/')) {
        return input;
    }
    try {
        return await imageCompression(input, DEFAULTS);
    } catch (error) {
        console.warn('[image-compression] compression failed, uploading original:', error);
        return input;
    }
}

/**
 * Compress a base64 payload (as produced by Capacitor Camera on web)
 * and return a fresh base64 string. Used by WebPhotoPickerStrategy to
 * post-process Capacitor's best-effort scaling.
 */
export async function compressBase64ToBase64(
    base64: string,
    sourceMimeType: string = 'image/jpeg'
): Promise<string> {
    const blob = base64ToBlob(base64, sourceMimeType);
    const file = new File([blob], 'capture.jpg', { type: sourceMimeType });
    const compressed = await compressImageFile(file);
    return await fileToBase64(compressed);
}

function base64ToBlob(base64: string, mimeType: string): Blob {
    const cleaned = base64.includes(',') ? base64.split(',')[1] : base64;
    const bytes = atob(cleaned);
    const buffer = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) {
        buffer[i] = bytes.charCodeAt(i);
    }
    return new Blob([buffer], { type: mimeType });
}

function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result as string;
            // Strip the `data:image/...;base64,` prefix so callers can
            // re-assemble it for their own use, matching the shape the
            // pre-existing PhotoService consumers expect.
            const stripped = result.includes(',') ? result.split(',')[1] : result;
            resolve(stripped);
        };
        reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
        reader.readAsDataURL(file);
    });
}
